import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Loader2, Check } from 'lucide-react';
import { api, Duty, Store, Staff } from '../../api';
import Modal from '../ui/Modal';
import Dialog from '../ui/Dialog';
import Input from '../ui/Input';
import { dutyMark } from '../StaffDuties';
import { useToast } from '../../contexts/ToastContext';

interface DutySettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
}

// 対応業務（鍵の開け閉め・レジ締めなど）の登録と、スタッフごとの対応可否の一覧。
const DutySettingsTab: React.FC<DutySettingsTabProps> = ({ stores, myProfile, isSystemAdmin }) => {
  const { showToast } = useToast();
  const [duties, setDuties] = useState<Duty[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [storeId, setStoreId] = useState('');
  const [editing, setEditing] = useState<Duty | null>(null);
  const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const availableStores = stores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id));
  const labelClass = 'text-xs font-bold text-slate-500 mb-1.5 block';

  useEffect(() => {
    if (!storeId && availableStores.length > 0) setStoreId(availableStores[0].id);
  }, [availableStores, storeId]);

  const fetchData = async () => {
    try {
      const [d, st] = await Promise.all([api.getDuties(), api.getStaff()]);
      setDuties(d);
      setStaffList(st);
    } catch (e) {
      showToast('データの取得に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => api.subscribeStaff(() => { api.getStaff().then(setStaffList); }), []);

  const storeDuties = duties.filter(d => d.storeIds.includes(storeId));
  const storeStaff = staffList
    .filter(s => s.stores?.includes(storeId) && s.status !== 'resigned' && !s.isGuest)
    .sort((a, b) => (a.role === 'staff' ? 1 : 0) - (b.role === 'staff' ? 1 : 0));
  const holdersOf = (dutyId: string) => storeStaff.filter(s => (s.dutyIds || []).includes(dutyId));

  const toggleStaffDuty = async (staff: Staff, dutyId: string) => {
    const has = (staff.dutyIds || []).includes(dutyId);
    const next = { ...staff, dutyIds: has ? (staff.dutyIds || []).filter(id => id !== dutyId) : [...(staff.dutyIds || []), dutyId] };
    setStaffList(prev => prev.map(s => s.id === staff.id ? next : s));
    try {
      await api.saveStaff([next]);
    } catch (e) {
      setStaffList(prev => prev.map(s => s.id === staff.id ? staff : s));
      showToast('保存に失敗しました。', 'error');
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { showToast('業務名を入力してください。', 'error'); return; }
    if (editing.storeIds.length === 0) { showToast('対象店舗を1つ以上選んでください。', 'error'); return; }
    setIsSaving(true);
    try {
      const target = { ...editing, name: editing.name.trim() };
      await api.saveDuties([target]);
      setDuties(prev => prev.some(d => d.id === target.id) ? prev.map(d => d.id === target.id ? target : d) : [...prev, target]);
      setEditing(null);
      showToast('対応業務を保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (duty: Duty) => {
    setDialog({
      isOpen: true, title: '対応業務の削除',
      message: `「${duty.name}」を削除しますか？\nスタッフに登録されている対応可否も削除されます。`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await api.deleteDuty(duty.id);
          setDuties(prev => prev.filter(d => d.id !== duty.id));
          setStaffList(prev => prev.map(s => ({ ...s, dutyIds: (s.dutyIds || []).filter(id => id !== duty.id) })));
          showToast('削除しました。', 'success');
        } catch (e) {
          showToast('削除に失敗しました。', 'error');
        }
      }
    });
  };

  return (
    <div className="space-y-8 animate-fade-in-up">
      <Dialog {...dialog} type="confirm" isDestructive onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />

      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-slate-800">対応業務一覧</h2>
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)} aria-label="店舗" className="bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-600 outline-none">
            {availableStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={() => setEditing({ id: `duty_${Date.now()}`, name: '', storeIds: storeId ? [storeId] : [], requiredDaily: false })} className="bg-blue-600 text-white font-bold px-4 py-2 rounded-[4px] text-sm flex items-center gap-1.5 shadow-md whitespace-nowrap"><Plus size={16} />対応業務を追加</button>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">ポジションとは別に、鍵の開け閉めやレジ締めなど「できる・できない」で管理する業務を登録します。「毎日1名以上必要」にした業務は、シフト作成で対応できる人がいない日に警告します。</p>

      <div className="bg-white shadow-sm border border-gray-200 overflow-x-auto rounded-[4px]">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-slate-500 font-bold border-b border-gray-200 text-xs">
            <tr>
              <th className="p-4 whitespace-nowrap">マーク</th>
              <th className="p-4 whitespace-nowrap">業務名</th>
              <th className="p-4 whitespace-nowrap text-center">毎日1名以上必要</th>
              <th className="p-4 whitespace-nowrap">対応できるスタッフ</th>
              <th className="p-4 whitespace-nowrap text-center">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={20} />読み込んでいます。</td></tr>
            ) : storeDuties.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400 font-bold text-xs">対応業務はまだありません。</td></tr>
            ) : storeDuties.map(duty => {
              const holders = holdersOf(duty.id);
              return (
                <tr key={duty.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4"><span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1 rounded-[3px] border border-blue-300 bg-blue-50 text-blue-700 text-xs font-bold whitespace-nowrap">{dutyMark(duty)}</span></td>
                  <td className="p-4 font-bold text-slate-800 whitespace-nowrap">{duty.name}</td>
                  <td className="p-4 text-center whitespace-nowrap">
                    {duty.requiredDaily ? <span className="text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">必要</span> : <span className="text-[11px] font-bold text-slate-400">指定なし</span>}
                    {duty.maxConcurrent ? <span className="ml-1 text-[11px] font-bold text-orange-600 bg-orange-50 border border-orange-200 rounded px-2 py-0.5 whitespace-nowrap">同時{duty.maxConcurrent}名まで</span> : null}
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className={`text-[11px] font-bold mr-1 whitespace-nowrap ${holders.length <= 2 ? 'text-orange-600' : 'text-slate-500'}`}>{holders.length}名</span>
                      {holders.map(s => <span key={s.id} className="text-[11px] font-bold text-slate-600 bg-slate-100 rounded px-2 py-0.5 whitespace-nowrap">{s.lastName} {s.firstName}</span>)}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-1">
                      <button onClick={() => setEditing({ ...duty })} aria-label={`${duty.name}を編集`} className="p-2 text-blue-600 hover:bg-blue-50 rounded-[4px]"><Edit size={18} /></button>
                      <button onClick={() => handleDelete(duty)} aria-label={`${duty.name}を削除`} className="p-2 text-gray-400 hover:text-red-600 rounded-[4px]"><Trash2 size={18} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {storeDuties.length > 0 && (
        <section data-testid="duty-matrix">
          <h3 className="text-base font-bold text-slate-800 mb-1">スタッフ別の対応表</h3>
          <p className="text-xs text-slate-500 mb-3">マスをクリックすると、対応できる・できないを切り替えられます。</p>
          <div className="bg-white shadow-sm border border-gray-200 overflow-x-auto rounded-[4px]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-slate-500 font-bold border-b border-gray-200 text-xs">
                <tr>
                  <th className="p-3 text-left whitespace-nowrap sticky left-0 bg-gray-50">スタッフ</th>
                  {storeDuties.map(d => <th key={d.id} className="p-3 text-center whitespace-nowrap">{d.name}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {storeStaff.map(s => (
                  <tr key={s.id}>
                    <td className="p-3 font-bold text-slate-700 whitespace-nowrap sticky left-0 bg-white">{s.lastName} {s.firstName}</td>
                    {storeDuties.map(d => {
                      const has = (s.dutyIds || []).includes(d.id);
                      return (
                        <td key={d.id} className="p-1.5 text-center">
                          <button type="button" onClick={() => toggleStaffDuty(s, d.id)} aria-pressed={has} aria-label={`${s.lastName} ${s.firstName}：${d.name}`}
                            className={`w-full min-w-[72px] py-1.5 rounded text-xs font-bold border transition-colors ${has ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-300 hover:border-slate-300'}`}>
                            {has ? '対応可' : '不可'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing && duties.some(d => d.id === editing.id) ? '対応業務の編集' : '対応業務の追加'} maxWidth="max-w-md">
        {editing && (
          <div className="space-y-6 pt-2">
            <Input label="業務名" value={editing.name} placeholder="例：鍵の開け閉め、レジ締め" onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <Input label="マーク（1〜2文字）" value={editing.mark || ''} maxLength={2} placeholder={editing.name ? editing.name.slice(0, 1) : '例：鍵'} onChange={(e) => setEditing({ ...editing, mark: e.target.value.slice(0, 2) })} />
            <p className="text-xs text-slate-500 -mt-4">シフト表や候補一覧で、対応できるスタッフの名前の横に表示します。</p>
            <div>
              <label className={labelClass}>対象店舗</label>
              <div className="flex flex-wrap gap-2">
                {availableStores.map(store => {
                  const selected = editing.storeIds.includes(store.id);
                  return (
                    <button key={store.id} type="button" onClick={() => setEditing({ ...editing, storeIds: selected ? editing.storeIds.filter(id => id !== store.id) : [...editing.storeIds, store.id] })}
                      className={`px-3 py-1.5 rounded-[4px] text-xs font-bold border flex items-center gap-1 ${selected ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-300'}`}>
                      {selected && <Check size={12} />}{store.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Input label="同じ時間帯の上限人数（任意）" type="number" min={1} value={editing.maxConcurrent ? String(editing.maxConcurrent) : ''} placeholder="例：インチャージは1" onChange={(e) => setEditing({ ...editing, maxConcurrent: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })} />
              <p className="text-xs text-slate-500 -mt-4">超えると警告し、自動作成では超えないように割り当てます。</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!editing.requiredDaily} onChange={(e) => setEditing({ ...editing, requiredDaily: e.target.checked })} className="w-5 h-5" />
              <span className="text-sm font-bold text-slate-700">毎日1名以上必要</span>
            </label>
            <div className="pt-4 flex justify-end gap-3 border-t">
              <button onClick={() => setEditing(null)} className="px-6 py-2.5 text-slate-500 font-bold">キャンセル</button>
              <button onClick={handleSave} disabled={isSaving} className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-[4px] shadow-md flex items-center gap-2">{isSaving && <Loader2 size={16} className="animate-spin" />}保存</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DutySettingsTab;
